import React, { useEffect } from 'react'
import * as d3 from 'd3'
import { ImpactDestinationsWrapper } from './style'

const D3BubbleGraph = (props) => {
  const {
    id,
    isTriangulation,
    type,
    graphDetails,
    tabChanged,
    domainValues,
    data,
    legends,
  } = props

  // =========================================================================
  // GLOBAL LAYOUT CONFIGURATIONS & SETTINGS
  // =========================================================================
  const settings = {
    dimensions: {
      height: 450,
      margin: { top: 20, right: 20, bottom: 20, left: 20 },
    },
    scales: {
      rMin: 25,
      rMax: 60,
      domainRMin: 5,
      domainRMax: 100,
    },
    labels: {
      fontSize: 12,
      fontFamily: 'sans-serif',
      estimatedCharWidth: 7, // Fallback width calculation per character
      defaultHeight: 14,     // Fallback text line bounds height
      spacing: 12,           // Padding between adjacent text nodes (3b)
      boundaryPadding: 15,   // Chart edge containment padding offset
    },
    collisions: {
      maxIterations: 180,     // Loop thresholds for resolving overlaps
      initialRadiusMultiplier: 1.6, // Multiplier for initial radial layout spacing
      minAnchorClearance: 25, // Absolute min px space away from any non-parent circle
    },
    lines: {
      strokeWidth: 1.5,
      slopeAngle: 20 * Math.PI / 180, // Elbow line angle
      maxOffsetRatio: 0.9,
    }
  }

  let scalingValX = isTriangulation ? 4 : 1.5

  const getColorForSentiments = (sentiment, shade = 'light') => {
    if (shade === 'light') {
      if (isTriangulation) {
        return legends.find((f) => f.value === sentiment)?.colorLight || 'rgba(166, 166, 166, 0.4)'
      } else {
        return sentiment === 'Positive'
          ? 'rgba(0, 176, 80, 0.4)'
          : sentiment === 'Negative'
            ? 'rgba(255, 0, 0, 0.4)'
            : 'rgba(166, 166, 166, 0.4)'
      }
    } else if (shade === 'dark') {
      if (isTriangulation) {
        return legends.find((f) => f.value === sentiment)?.colorDark || 'rgba(166, 166, 166, 1)'
      } else {
        return sentiment === 'Positive'
          ? 'rgba(0, 176, 80, 1)'
          : sentiment === 'Negative'
            ? 'rgba(255, 0, 0, 1)'
            : 'rgba(166, 166, 166, 1)'
      }
    }
  }

  const onLoadFunc = () => {
    const idWrapper = document.getElementById(id)
    if (!idWrapper) return
    idWrapper.innerHTML = ''

    if (isTriangulation) {
      if (idWrapper.clientWidth < 1900) scalingValX = 8
      if (idWrapper.clientWidth < 1400) scalingValX = 9
      if (idWrapper.clientWidth < 1200) scalingValX = 11
      if (idWrapper.clientWidth < 1000) scalingValX = 15
    }

    const width = idWrapper?.offsetWidth || 800
    const height = settings.dimensions.height
    const margin = settings.dimensions.margin
    const wrapperTop = idWrapper.offsetTop - 50
    const wrapperLeft = idWrapper.offsetLeft - 50

    const escapeSelector = (str) => encodeURIComponent(str).replace(/%/g, '_')

    // -----------------------------------------------------------------------
    // STEP 1: CREATE THE SVG
    // -----------------------------------------------------------------------
    const svg = d3
      .select(idWrapper)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height + 50}`)
      .attr('preserveAspectRatio', 'xMinYMin')
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`)

    // Sort Data Structure
    data.sort((a, b) => {
      if (a['MeanRatingIndex'] === b['MeanRatingIndex']) {
        return a['BuzzVolumeIndex'] - b['BuzzVolumeIndex']
      }
      return a['MeanRatingIndex'] - b['MeanRatingIndex']
    })

    // Create Scaling Maps
    const xScale = d3
      .scaleLinear()
      .domain([
        d3.min(data, (d) => tabChanged === 0 ? parseInt(d.BuzzVolumeIndex - scalingValX) : domainValues.xMin),
        d3.max(data, (d) => tabChanged === 0 ? parseInt(d.BuzzVolumeIndex + scalingValX) : domainValues.xMax),
      ])
      .range([0, width - (margin.left + margin.right)])

    const yScale = d3
      .scaleLinear()
      .domain([domainValues.yMin, domainValues.yMax])
      .range([height - (margin.top + margin.bottom), 0])

    const rScale = d3
      .scaleSqrt()
      .domain([settings.scales.domainRMin, settings.scales.domainRMax])
      .range([settings.scales.rMin, settings.scales.rMax])

    // Print Grid Lines Backend Mapping
    svg.append('g').attr('class', 'horizontalLine').selectAll('line').data(data).join('line')
      .attr('x1', wrapperLeft).attr('y1', yScale(graphDetails.AxisCutHorizontal))
      .attr('x2', width + 200 + 'px').attr('y2', yScale(graphDetails.AxisCutHorizontal))
      .attr('stroke-width', 1).attr('stroke', 'rgba(126, 126, 126, 1)').attr('stroke-dasharray', '4,4')
      .style('opacity', (d, i) => (i === 0 ? 0.5 : 0))

    svg.append('g').attr('class', 'verticalLine').selectAll('line').data(data).join('line')
      .attr('x1', xScale(graphDetails.AxisCutVertical)).attr('y1', wrapperTop)
      .attr('x2', xScale(graphDetails.AxisCutVertical)).attr('y2', yScale(domainValues.yMin) + 100) // FIXED: Matches calculation layout base limit
      .attr('stroke-width', 1).attr('stroke', 'rgba(126, 126, 126, 1)').attr('stroke-dasharray', '4,4')
      .style('opacity', (d, i) => (i === 0 ? 0.5 : 0))

    // Precalculate coordinates & anchor metadata structures
    const anchorArray = []
    const labelArray = []
    const groupYear = {}

    data.forEach((d, i) => {
      const cx = xScale(+d['BuzzVolumeIndex'])
      const cy = yScale(d['MeanRatingIndex'])
      const r = rScale(+d['PercentEngagement'])

      anchorArray.push({ x: cx, y: cy, r: r })

      // Generate initial label layouts placed dynamically in an outer ring path topology
      const radialAngle = (i * 2 * Math.PI) / data.length
      const spawnDistance = r * settings.collisions.initialRadiusMultiplier

      labelArray.push({
        x: cx + Math.cos(radialAngle) * spawnDistance,
        y: cy + Math.sin(radialAngle) * spawnDistance,
        name: d['DriverName'],
        width: d['DriverName'].length * settings.labels.estimatedCharWidth,
        height: settings.labels.defaultHeight,
        sentiment: d.Sentiment,
        parentIndex: i
      })
    })

    // -----------------------------------------------------------------------
    // STEP 2: PLACE THE CIRCLES
    // -----------------------------------------------------------------------
    // Outer Borders Mapping
    svg.selectAll('.outer-border')
      .data(data)
      .enter()
      .append('circle')
      .attr('class', 'outer-border')
      .attr('data-driver', (d) => escapeSelector(d.DriverName))
      .attr('cx', (d) => xScale(+d['BuzzVolumeIndex']))
      .attr('cy', (d) => yScale(d['MeanRatingIndex']))
      .attr('r', (d) => rScale(+d['PercentEngagement']) + 4)
      .attr('fill', 'none')
      .attr('stroke', (d) => getColorForSentiments(d.Sentiment, 'dark'))
      .attr('stroke-width', 2)
      .style('opacity', 0.7)
      .style('pointer-events', 'none')

    // Inner Interactive Main Matrix System
    const mainCircles = svg.selectAll('.main-circle')
      .data(data)
      .enter()
      .append('circle')
      .attr('class', 'main-circle')
      .attr('data-driver', (d) => escapeSelector(d.DriverName))
      .attr('cx', (d) => xScale(+d['BuzzVolumeIndex']))
      .attr('cy', (d) => yScale(d['MeanRatingIndex']))
      .attr('r', (d) => rScale(+d['PercentEngagement']))
      .attr('fill-opacity', (d) => {
        const cxStr = xScale(+d['BuzzVolumeIndex']).toString()
        if (!groupYear[cxStr]) groupYear[cxStr] = 0
        groupYear[cxStr]++
        return groupYear[cxStr] > 2 ? 0.3 : groupYear[cxStr] === 2 ? 0.5 : 0.7
      })
      .style('fill', (d) => getColorForSentiments(d.Sentiment))
      .style('opacity', 0.7)

    // Element Popup Interaction Logic Handler
    const elementClickShowPopupFunc = (events, dObject) => {
      const thsWrapperId = idWrapper.parentNode
      const popupId = `${type}_popup`
      if (dObject?.Insights) {
        const wrapperDetails = idWrapper.getBoundingClientRect()
        const position = {
          x: events.clientX - (window.scrollX + wrapperDetails.left),
          y: events.clientY - (window.scrollY + wrapperDetails.top),
        }
        if (position.y < 40) position.y = 70
        const content = `
          <div class='overlay' id='popupClose'></div>
          <div class='popup' id='${popupId}' style='top:${position.y}px; left:${position.x}px; max-height:${wrapperDetails.height - 20}px; overflow-y:auto'>
            <a class='close' href='javascript:' id='close'>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 1L9 9" stroke="#C3C3C3" stroke-width="1.5" stroke-linecap="round"/><path d="M9 1L1 9" stroke="#C3C3C3" stroke-width="1.5" stroke-linecap="round"/></svg>
            </a>
            <p class='driverName'>${dObject.DriverName}</p>
            <div class='description'>${dObject?.Insights}</div>
          </div>`
        thsWrapperId.querySelector('.tooltip').innerHTML = content
        thsWrapperId.querySelector('a#close').addEventListener('click', () => thsWrapperId.querySelector('.tooltip').innerHTML = '')
        thsWrapperId.querySelector('div#popupClose').addEventListener('click', () => thsWrapperId.querySelector('.tooltip').innerHTML = '')
      }
    }

    // Bind handlers to main circles
    mainCircles.on('mouseover', function (event, d) {
      const driverKey = escapeSelector(d.DriverName)
      d3.select(this).style('fill', getColorForSentiments(d.Sentiment, 'dark')).style('opacity', 1)
      svg.select(`.label[data-driver="${driverKey}"]`).style('font-weight', 'bold')
      svg.select(`.outer-border[data-driver="${driverKey}"]`).style('opacity', 1)
      svg.select(`.link[data-driver="${driverKey}"]`).style('opacity', 1)
    })
    .on('mouseout', function (event, d) {
      const driverKey = escapeSelector(d.DriverName)
      d3.select(this).style('fill', getColorForSentiments(d.Sentiment)).style('opacity', 0.7)
      svg.select(`.label[data-driver="${driverKey}"]`).style('font-weight', '400')
      svg.select(`.outer-border[data-driver="${driverKey}"]`).style('opacity', 0.7)
      svg.select(`.link[data-driver="${driverKey}"]`).style('opacity', 0.7)
    })
    .on('click', function (events, d) {
      elementClickShowPopupFunc(events, d)
    })

    // -----------------------------------------------------------------------
    // STEP 3: RESOLVE LABELS POSITION MATRIX CRITERIAS (No overlaps allowed)
    // -----------------------------------------------------------------------
    function enforceRules() {
      let moved = false

      for (let i = 0; i < labelArray.length; i++) {
        const l = labelArray[i]
        const w = l.width
        const h = l.height

        // CRITERIA A: Label must not overlap ANY circle bounding area
        for (let j = 0; j < anchorArray.length; j++) {
          const c = anchorArray[j]
          const padding = (l.parentIndex === j) ? (c.r + 30) : (c.r + settings.collisions.minAnchorClearance)

          // Nearest point bounding math logic mapping
          const closestX = Math.max(l.x, Math.min(c.x, l.x + w))
          const closestY = Math.max(l.y - h, Math.min(c.y, l.y))
          const distance = Math.hypot(closestX - c.x, closestY - c.y)
          const inside = (c.x >= l.x && c.x <= l.x + w && c.y >= l.y - h && c.y <= l.y)

          if (distance < padding || inside) {
            let pushX = (l.x + w / 2) - c.x
            let pushY = (l.y - h / 2) - c.y
            const len = Math.hypot(pushX, pushY) || 1

            const scalePush = inside ? padding + 15 : (padding - distance)
            l.x += (pushX / len) * scalePush
            l.y += (pushY / len) * scalePush
            moved = true
          }
        }

        // CRITERIA B: Label must not overlap other Labels
        for (let k = 0; k < labelArray.length; k++) {
          if (i === k) continue
          const target = labelArray[k]
          const sp = settings.labels.spacing

          const overlapX = (l.x < target.x + target.width + sp) && (l.x + w + sp > target.x)
          const overlapY = (l.y - h < target.y + sp) && (l.y + sp > target.y - target.height)

          if (overlapX && overlapY) {
            const minX = Math.min((target.x + target.width + sp) - l.x, (l.x + w + sp) - target.x)
            const minY = Math.min((target.y + sp) - (l.y - h), (l.y + sp) - (target.y - target.height))

            if (minX < minY) {
              const sign = (l.x + w / 2 > target.x + target.width / 2) ? 1 : -1
              l.x += sign * (minX / 2)
              target.x -= sign * (minX / 2)
            } else {
              const sign = (l.y - h / 2 > target.y - target.height / 2) ? 1 : -1
              l.y += sign * (minY / 2)
              target.y -= sign * (minY / 2)
            }
            moved = true
          }
        }

        // Contain labels inside outer canvas frame limits safely
        const minCanvasW = settings.labels.boundaryPadding
        const maxCanvasW = width - (margin.left + margin.right) - w - settings.labels.boundaryPadding
        const minCanvasY = h + settings.labels.boundaryPadding
        const maxCanvasY = height - (margin.top + margin.bottom) - settings.labels.boundaryPadding

        l.x = Math.max(minCanvasW, Math.min(maxCanvasW, l.x))
        l.y = Math.max(minCanvasY, Math.min(maxCanvasY, l.y))
      }
      return moved
    }

    // Run geometric simulation loops to solve layouts cleanly
    for (let loop = 0; loop < settings.collisions.maxIterations; loop++) {
      if (!enforceRules()) break
    }

    // Now render the settled labels inside the DOM
    const labels = svg.selectAll('.label')
      .data(labelArray)
      .enter()
      .append('text')
      .attr('class', 'label')
      .attr('data-driver', (d) => escapeSelector(d.name))
      .text((d) => d.name)
      .attr('font-family', settings.labels.fontFamily)
      .attr('font-size', settings.labels.fontSize)
      .attr('x', (d) => d.x)
      .attr('y', (d) => d.y)
      .attr('text-anchor', 'start')

    // Update real widths / heights inside text bounds structures safely
    labels.each(function (d, i) {
      const bbox = this.getBBox()
      labelArray[i].width = bbox.width || labelArray[i].width
      labelArray[i].height = bbox.height || labelArray[i].height
    })

    // Run secondary alignment match to refine coordinates against true bounding boxes
    for (let pass = 0; pass < 25; pass++) { enforceRules() }
    labels.attr('x', (d) => d.x).attr('y', (d) => d.y)

    // Bind mouse triggers to text nodes
    labels.on('mouseover', function (event, d) {
      const driverKey = escapeSelector(d.name)
      svg.select(`.main-circle[data-driver="${driverKey}"]`).style('fill', getColorForSentiments(d.sentiment, 'dark')).style('opacity', 1)
      d3.select(this).style('font-weight', 'bold')
      svg.select(`.outer-border[data-driver="${driverKey}"]`).style('opacity', 1)
      svg.select(`.link[data-driver="${driverKey}"]`).style('opacity', 1)
    })
    .on('mouseout', function (event, d) {
      const driverKey = escapeSelector(d.name)
      svg.select(`.main-circle[data-driver="${driverKey}"]`).style('fill', getColorForSentiments(d.sentiment)).style('opacity', 0.7)
      d3.select(this).style('font-weight', '400')
      svg.select(`.outer-border[data-driver="${driverKey}"]`).style('opacity', 0.7)
      svg.select(`.link[data-driver="${driverKey}"]`).style('opacity', 0.7)
    })
    .on('click', function (events, d) {
      const dObject = data.find((f) => f.DriverName === d.name)
      elementClickShowPopupFunc(events, dObject)
    })

    // -----------------------------------------------------------------------
    // STEP 4: PLACE THE CONNECTING ELBOW LINES
    // -----------------------------------------------------------------------
    svg.selectAll('.link')
      .data(labelArray)
      .enter()
      .append('path')
      .attr('class', 'link')
      .attr('data-driver', (d) => escapeSelector(d.name))
      .attr('fill', 'none')
      .attr('stroke', (d) => getColorForSentiments(d.sentiment, 'dark'))
      .attr('stroke-width', settings.lines.strokeWidth)
      .style('opacity', 0.7)
      .attr('d', (d) => {
        const anchor = anchorArray[d.parentIndex]
        const startR = anchor.r + 4

        const dx = d.x - anchor.x
        const dy = d.y - anchor.y
        const angle = Math.atan2(dy, dx)

        const startX = anchor.x + Math.cos(angle) * startR
        const startY = anchor.y + Math.sin(angle) * startR

        const clampedY = Math.max(d.y - d.height, Math.min(d.y, startY))
        const leftEdgePoint = { x: d.x, y: clampedY }
        const rightEdgePoint = { x: d.x + d.width, y: clampedY }

        const distToLeft = Math.hypot(startX - leftEdgePoint.x, startY - leftEdgePoint.y)
        const distToRight = Math.hypot(startX - rightEdgePoint.x, startY - rightEdgePoint.y)
        const attachPoint = distToLeft <= distToRight ? leftEdgePoint : rightEdgePoint

        const verticalDist = attachPoint.y - startY
        let horizontalOffset = verticalDist / Math.tan(settings.lines.slopeAngle)
        if (Math.abs(verticalDist) < 0.1) horizontalOffset = 0

        const sign = (attachPoint.x - startX) > 0 ? 1 : -1
        const maxOffset = Math.abs(attachPoint.x - startX) * settings.lines.maxOffsetRatio
        let elbowX = startX + sign * Math.min(Math.abs(horizontalOffset), maxOffset)

        if ((sign > 0 && elbowX > attachPoint.x) || (sign < 0 && elbowX < attachPoint.x)) {
          elbowX = attachPoint.x
        }

        return `M${startX},${startY} L${elbowX},${startY} L${attachPoint.x},${attachPoint.y}`
      })

    const colLine = idWrapper.parentNode?.querySelector('.columnLine span')
    if (colLine) {
      const columnWidth = colLine.offsetWidth
      colLine.style.left = -(columnWidth / 2) + 'px'
    }
  }

  useEffect(() => {
    onLoadFunc()
  }, [data, id, tabChanged, domainValues, legends, graphDetails])

  useEffect(() => {
    let resizeTimer
    const handleResize = () => {
      clearTimeout(resizeTimer)
      resizeTimer = setTimeout(() => {
        onLoadFunc()
      }, 150)
    }
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      clearTimeout(resizeTimer)
    }
  }, [data, id, tabChanged, domainValues, legends, graphDetails])

  return (
    <ImpactDestinationsWrapper id={`${id}_Wrapper`}>
      <div className='svgWrapper' id={`${id}`}></div>
      <div className='columnLine'>
        <span className='old'>{graphDetails?.AxisNameVertical}</span>
      </div>
      <div className='rowLine'>
        <span>{graphDetails?.AxisNameHorizontal}</span>
      </div>
      <div className='tooltip'></div>
    </ImpactDestinationsWrapper>
  )
}

export default D3BubbleGraph
